import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { catchError, map, of, switchMap, tap } from 'rxjs';
import * as DocumentActions from './document.actions';
import { TableNames } from '../../services/utils/dexie.utils';
import { DocumentService } from '../../services/document.service';
import { DexieService } from '../../services/dexie.service';
import { SecureDocumentUploadService } from '../../services/secure-document-upload.service';
import { Document } from '../../services/model';

@Injectable({
  providedIn: 'root',
})
export class DocumentEffects {
  loadDocuments$;
  loadDocument$;
  uploadDocument$;
  cancelUpload$;
  deleteDocument$;

  constructor(
    private readonly actions$: Actions,
    private readonly store: Store,
    private readonly documentService: DocumentService,
    private readonly secureDocumentUploadService: SecureDocumentUploadService,
    private readonly dexieService: DexieService
  ) {
    this.loadDocuments$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DocumentActions.fetchDocuments),
        switchMap(() => this.dexieService.allItems<Document>(TableNames.DOCUMENT).pipe(catchError(() => of([])))),
        tap((documents) => {
          if (documents.length > 0) {
            this.store.dispatch(DocumentActions.loadDocuments({ documents }));
          }
        }),
        switchMap(() => this.documentService.getAll()),
        switchMap((documents) =>
          this.dexieService.updateTable(TableNames.DOCUMENT, documents).pipe(
            map(() => documents),
            catchError(() => of(documents))
          )
        ),
        map((documents) => DocumentActions.loadDocuments({ documents })),
        catchError((error) => {
          console.error('Error loading documents:', error);
          return of(DocumentActions.loadDocumentFailed({ error: error.message || 'Failed to load documents' }));
        })
      )
    );

    this.loadDocument$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DocumentActions.fetchDocument),
        switchMap(({ id }) =>
          this.dexieService.itemById<Document>(TableNames.DOCUMENT, id).pipe(
            tap((doc) => {
              if (doc) {
                this.store.dispatch(DocumentActions.loadDocument({ document: doc }));
              }
            }),
            switchMap(() => this.documentService.getById(id)),
            tap((doc) => this.dexieService.addOrUpdateItems<Document>(TableNames.DOCUMENT, [doc])),
            map((doc) => DocumentActions.loadDocument({ document: doc })),
            catchError((error) => of(DocumentActions.loadDocumentFailed({ error })))
          )
        )
      )
    );

    this.uploadDocument$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DocumentActions.uploadDocument),
        switchMap(({ document }) => {
          // Use the secure upload service
          const uploadObservable = this.secureDocumentUploadService.uploadFile(
            document.file as File,
            document.documentType?.id,
            {
              filename: document.filename,
              description: (document as any).description || '',
              tenantId: document.tenantId,
            }
          );

          return uploadObservable.pipe(
            switchMap((progress) => {
              // Handle different upload states
              if (progress.status === 'completed') {
                this.store.dispatch(DocumentActions.fetchDocuments());
                return of(
                  DocumentActions.uploadDocumentSuccess({
                    documentName: document.fileType,
                    uploadId: progress.uploadId,
                  })
                );
              } else if (progress.status === 'failed') {
                return of(
                  DocumentActions.uploadDocumentFailure({
                    error: progress.error || 'Upload failed',
                    uploadId: progress.uploadId,
                  })
                );
              } else {
                // Upload in progress - emit progress update
                return of(DocumentActions.uploadDocumentProgress({ progress }));
              }
            }),
            catchError((error) =>
              of(
                DocumentActions.uploadDocumentFailure({
                  error: error.message || 'Upload failed',
                  uploadId: 'unknown',
                })
              )
            )
          );
        })
      )
    );

    // New effect for handling upload cancellation
    this.cancelUpload$ = createEffect(
      () =>
        this.actions$.pipe(
          ofType(DocumentActions.cancelUpload),
          tap(({ uploadId }) => {
            this.secureDocumentUploadService.cancelUpload(uploadId);
          })
        ),
      { dispatch: false }
    );

    this.deleteDocument$ = createEffect(() =>
      this.actions$.pipe(
        ofType(DocumentActions.deleteDocument),
        switchMap(({ id }) =>
          this.documentService.delete(id).pipe(
            switchMap(() => this.dexieService.deleteItem(TableNames.DOCUMENT, id)),
            map(() => DocumentActions.deleteDocumentSuccess({ id })),
            catchError((error) =>
              of(DocumentActions.deleteDocumentFailure({ error: error.message || 'Delete failed' }))
            )
          )
        )
      )
    );
  }
}
